import React from "react";
import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../../UI/Loader/Loader";
import { Button } from "../../../../../UI/Button/Button";
import { pinLength } from "../../../../../../constants/pinLength";
import { InputText } from "../../../../../UI/InputText/InputText";
import { InputEmail } from "../../../../../UI/InputEmail/InputEmail";
import { isValidPhone } from "../../../../../../utils/is-valid-phone";
import { startNumber } from "../../../../../../model/system/constants";
import { useInputChange } from "../../../../../../hooks/useInputChange";
import { getTransPosition, USERPOSITIONS } from "constants/userPositions";
import { ISystemState } from "../../../../../../model/system/systemReducer";
import { getStoreList } from "../../../../../../model/dashboardStores/dStoresAPI";
import { IdUsersState } from "../../../../../../model/dashboardUsers/dUsersReducer";
import { InputNumberLimit } from "../../../../../UI/InputNumberLimit/InputNumberLimit";
import { InputMaskedPhone } from "../../../../../UI/InputMaskedPhone/InputMaskedPhone";
import { dUsersAddItemSagaAC } from "../../../../../../model/dashboardUsers/dUsersActionCreators";
import {
  convertToHash,
  validateEmail,
  validatePassword,
} from "../../../../../utils";
import {
  SelectBox,
  NULL_VALUE,
  typeValuesListItem,
} from "../../../../../UI/SelectBox/SelectBox";
import {
  searchUsersByEmail,
  getUsersPositionList, checkEmailExists,
} from "../../../../../../model/dashboardUsers/dUsersAPI";
import {
  logoutSagaAC,
  addNotificationAC,
} from "../../../../../../model/system/systemActionCreators";

export type typePopupEdit = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupAdd: React.FC<typePopupEdit> = ({
  variant,
  setOpenPopup,
}) => {
  const dispatch = useDispatch();

  const filters = useSelector(
    (state: { dUsers: IdUsersState }) => state.dUsers.filters,
  );

  const position = useSelector(
    (state: { system: ISystemState }) => state.system.userInfo?.position,
  );

  const pagination = useSelector(
    (state: { dUsers: IdUsersState }) => state.dUsers.pagination,
  );

  const { value: pinValue, setValue: setPinValue } = useInputChange("");
  const { value: phoneValue, setValue: setPhoneValue } = useInputChange("");
  const {
    value: passwordValue,
    changeValueHandler: passwordChangeValueHandler,
  } = useInputChange("");
  const {
    value: lastNameValue,
    changeValueHandler: lastNameChangeValueHandler,
  } = useInputChange("");
  const {
    value: firstNameValue,
    changeValueHandler: firstNameChangeValueHandler,
  } = useInputChange("");
  const { value: emailValue, changeValueHandler: emailChangeValueHandler } =
    useInputChange("");

  const [loader, setLoader] = React.useState(false);
  const [startValidate, setStartValidate] = React.useState(false);
  const [existDeletedEmail, setExistDeletedEmail] = React.useState(false);
  const [storeIdValue, setStoreIdValue] = React.useState<string | null>(null);
  const [selectStoreList, setSelectStoreList] = React.useState<
    typeValuesListItem[]
  >([]);
  const [selectPositionList, setSelectPositionList] = React.useState<
    typeValuesListItem[]
  >([]);
  const [positionIdValue, setPositionIdValue] = React.useState<string | null>(
    null,
  );
  const [validationObj, setValidationObj] = React.useState({
    lenPIN: true,
    validPhone: true,
    validEmail: true,
    existEmail: false,
    requiredPIN: true,
    requiredEmail: true,
    validPassword: true,
    requiredStoreId: false,
    requiredPassword: true,
    requiredPosition: true,
    requiredLastName: false,
    requiredFirstName: false,
    existEmailInData: false
  });

  React.useEffect(() => {
    const setData = async () => {
      setLoader(true);
      let responseStores = await getStoreList({
        filters: { deleted: false, name: null },
        full: false,
      });

      if (responseStores.status === 200) {
        const newStoresList = [...selectStoreList, ...responseStores.data];
        setSelectStoreList(newStoresList);
      } else if (responseStores.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responsePositions = await getUsersPositionList({});

      if (responsePositions.status === 200) {
        let newPositionsList: {
          id: string;
          name: React.ReactElement<TransProps> | string;
        }[] = responsePositions.data.map((item: string) => ({
          id: item,
          name: getTransPosition(item) || item,
        }));
        if (position !== USERPOSITIONS.ADMIN) {
          newPositionsList = newPositionsList.filter(
            (item) => item.id !== USERPOSITIONS.ADMIN,
          );
        }
        setSelectPositionList(newPositionsList);
      } else if (responsePositions.status === 401) {
        dispatch(logoutSagaAC());
      }
      setLoader(false);
    };

    setData().catch(console.error);

    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (pinValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ requiredPIN: false } };
      });
    } else {
      if (pinValue.length === pinLength) {
        setValidationObj((v) => {
          return { ...v, ...{ lenPIN: true, requiredPIN: true } };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ lenPIN: false, requiredPIN: true } };
        });
      }
    }
  }, [pinValue]);

  React.useEffect(() => {
    if (phoneValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ validPhone: true } };
      });
    } else if (!isValidPhone(phoneValue)) {
      if (phoneValue === startNumber) {
        setValidationObj((v) => {
          return { ...v, ...{ validPhone: true } };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ validPhone: false } };
        });
      }
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ validPhone: true } };
      });
    }
  }, [phoneValue]);

  React.useEffect(() => {
    const trimEmail = emailValue.trim();
    if (trimEmail !== "") {
      if (validateEmail(trimEmail)) {
        setValidationObj((v) => {
          return {
            ...v,
            ...{ validEmail: true, requiredEmail: true, existEmail: false },
          };
        });
        setExistDeletedEmail(false);
      } else {
        setValidationObj((v) => {
          return {
            ...v,
            ...{ validEmail: false, requiredEmail: true, existEmail: false },
          };
        });
        setExistDeletedEmail(false);
      }
    } else {
      setValidationObj((v) => {
        return {
          ...v,
          ...{
            validEmail: false,
            requiredEmail: false,
            existEmail: false,
          },
        };
      });
      setExistDeletedEmail(false);
    }
  }, [emailValue]);

  React.useEffect(() => {
    if (storeIdValue !== null) {
      setValidationObj((v) => {
        return { ...v, ...{ requiredStoreId: true } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredStoreId: false } };
      });
    }
  }, [storeIdValue]);

  React.useEffect(() => {
    if (passwordValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ requiredPassword: false } };
      });
    } else {
      const validPassword = validatePassword(passwordValue.trim());
      if (validPassword) {
        setValidationObj((v) => {
          return { ...v, ...{ validPassword: true, requiredPassword: true } };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ validPassword: false, requiredPassword: true } };
        });
      }
    }
  }, [passwordValue]);

  React.useEffect(() => {
    if (lastNameValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ requiredLastName: false } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredLastName: true } };
      });
    }
  }, [lastNameValue]);

  React.useEffect(() => {
    if (firstNameValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ requiredFirstName: false } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredFirstName: true } };
      });
    }
  }, [firstNameValue]);

  React.useEffect(() => {
    if (positionIdValue === null || positionIdValue === NULL_VALUE.id) {
      setValidationObj((v) => {
        return { ...v, ...{ requiredPosition: false } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredPosition: true } };
      });
    }
  }, [positionIdValue]);

  const onAddHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoader(true);

    setStartValidate(true);

    if (isDisabled) {
      setLoader(false);
      return;
    }

    const uniqueValidate = (response: any) => {
      let validationValue = false;
      if (response.status === 200) {
        validationValue = response.data.length === 0;
        if (response.data.length && response.data[0].deleted === true) {
          setExistDeletedEmail(true);
        }
      } else {
        if (response.status === 404) {
          validationValue = true;
        } else if (response.status === 401) {
          dispatch(logoutSagaAC());
        } else {
          const id = new Date();

          let message =
            response?.data?.description ||
            response?.data?.message ||
            "ERROR_SERVER_CONNECTION";

          addNotificationAC({
            message,
            id: id.getTime().toString(),
          });
        }
      }
      return validationValue;
    };

    let isExistEmail: boolean;

    const responseEmail = await searchUsersByEmail({
      email: emailValue.trim(),
    });

    isExistEmail = !uniqueValidate(responseEmail);

    setValidationObj((v) => {
      return { ...v, existEmail: isExistEmail };
    });

    if (!isExistEmail){
      const res = await checkEmailExists( emailValue.trim() );

      if (res.data) {
        isExistEmail = res.data
        setValidationObj({ ...validationObj, ...{ existEmailInData: true } });
      }
    }

    if (!isExistEmail) {
      const dataAddObject = {
        pin: pinValue.trim() === "" ? null : pinValue.trim(),
        email: emailValue.trim() === "" ? null : emailValue.trim(),
        phoneNumber: !phoneValue.trim() ? null : phoneValue.trim(),
        storeId: storeIdValue === NULL_VALUE.id ? null : storeIdValue,
        password: passwordValue.trim() === "" ? null : passwordValue.trim(),
        lastName: lastNameValue.trim() === "" ? null : lastNameValue.trim(),
        position: positionIdValue === NULL_VALUE.id ? null : positionIdValue,
        firstName: firstNameValue.trim() === "" ? null : firstNameValue.trim(),
      };

      if (pinValue.trim() !== "") {
        await convertToHash(pinValue.trim()).then(
          (res) => (dataAddObject.pin = res),
        );
      } else {
        dataAddObject.pin = null;
      }

      dispatch(
        dUsersAddItemSagaAC(dataAddObject as any, filters, {
          page: pagination.pageNumber,
          size: pagination.itemsPerPage!,
        }),
      );
      setLoader(false);
      setOpenPopup(false);
      setStartValidate(false);
    } else {
      setLoader(false);
      return;
    }
  };

  const isDisabled = !(
    validationObj.requiredFirstName &&
    validationObj.requiredLastName &&
    validationObj.requiredPosition &&
    validationObj.validEmail &&
    validationObj.lenPIN &&
    validationObj.requiredPIN &&
    validationObj.requiredEmail &&
    validationObj.validPassword &&
    validationObj.validPhone &&
    validationObj.requiredPassword
  );

  return (
    <form onSubmit={(event) => onAddHandler(event)}>
      <div className="relative">
        <div className="relative grid gap-4 grid-cols-1 sm:grid-cols-2">
          <div className="sm:col-start-1 sm:col-end-3">
            <InputText
              id="firstName"
              maxLength={75}
              required={true}
              name="firstName"
              autoFocus={true}
              value={firstNameValue}
              labelClassName="text-left"
              label={<Trans>User name</Trans>}
              onChange={firstNameChangeValueHandler}
              errorMessage={
                startValidate ? (
                  validationObj.requiredFirstName ? undefined : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
          </div>

          <div className="sm:col-start-1 sm:col-end-3">
            <InputText
              id="lastName"
              maxLength={75}
              name="lastName"
              required={true}
              value={lastNameValue}
              labelClassName="text-left"
              label={<Trans>User last name</Trans>}
              onChange={lastNameChangeValueHandler}
              errorMessage={
                startValidate ? (
                  validationObj.requiredLastName ? undefined : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
          </div>

          <div>
            <InputMaskedPhone
              id="phone"
              name="phone"
              value={phoneValue}
              setValue={setPhoneValue}
              labelClassName="text-left"
              label={<Trans>Phone number</Trans>}
              errorMessage={
                startValidate ? (
                  validationObj.validPhone ? undefined : (
                    <Trans>Enter valid phone number</Trans>
                  )
                ) : undefined
              }
            />
          </div>

          <div>
            <InputEmail
              id="email"
              name="email"
              maxLength={50}
              required={true}
              value={emailValue}
              labelClassName="text-left"
              label={<Trans>Email</Trans>}
              onChange={emailChangeValueHandler}
              errorMessage={
                startValidate ? (
                  validationObj.requiredEmail ? (
                    validationObj.validEmail ? undefined : (
                      <Trans>Enter valid email</Trans>
                    )
                  ) : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
            {validationObj.existEmailInData && !existDeletedEmail && (
                <p className="text-xs mt-1 text-red-500 col-span-2 text-left">
                  <Trans>User with this email already exists.</Trans>
                </p>
            )}
            {validationObj.existEmail && !existDeletedEmail && (
              <p className="text-xs mt-1 text-red-500 col-span-2 text-left">
                <Trans>User with this email already exists.</Trans>
                <br />
                <Trans> You can find it using the filter.</Trans>
              </p>
            )}
            {existDeletedEmail && (
              <p className="text-xs mt-1 text-red-500 col-span-2 text-left">
                <Trans>User with this email was deleted.</Trans>
                <br />
                <Trans>
                  You can find it using the filter and recovery.
                  <Trans>
                    <br />
                  </Trans>
                  Or use another email.
                </Trans>
              </p>
            )}
          </div>

          <div>
            <SelectBox
              valueId={positionIdValue}
              labelClassname="text-left"
              setIdValue={setPositionIdValue}
              label={<Trans>Position</Trans>}
              valuesList={selectPositionList}
              required={true}
              errorMessage={
                startValidate ? (
                  validationObj.requiredPosition ? undefined : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
          </div>

          <div>
            <SelectBox
              valueId={storeIdValue}
              labelClassname="text-left"
              setIdValue={setStoreIdValue}
              valuesList={selectStoreList}
              label={<Trans>Store</Trans>}
            />
          </div>

          <div>
            <InputText
              type="text"
              id="password"
              maxLength={50}
              name="password"
              required={true}
              value={passwordValue}
              labelClassName="text-left"
              label={<Trans>User password</Trans>}
              onChange={passwordChangeValueHandler}
              errorMessage={
                startValidate ? (
                  validationObj.requiredPassword ? undefined : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
            <p
              className={`${
                validatePassword(passwordValue.trim())
                  ? "opacity-0"
                  : "opacity-100"
              } text-xs mt-1 text-${
                !validationObj.validPassword ? "red" : "gray"
              }-500`}
            >
              <Trans>
                The password must be 5-10 characters long and at least 1 digit
                and have no spaces.
              </Trans>
            </p>
          </div>

          <div>
            <InputNumberLimit
              id="pin"
              name="pin"
              required={true}
              value={pinValue}
              setValue={setPinValue}
              labelClassName="text-left"
              digitBeforeDot={pinLength}
              label={<Trans>PIN code for cash desk</Trans>}
              errorMessage={
                startValidate ? (
                  validationObj.requiredPIN ? undefined : (
                    <Trans>Required field</Trans>
                  )
                ) : undefined
              }
            />
            <p
              className={`${
                startValidate && validationObj.lenPIN
                  ? "opacity-0"
                  : "opacity-100"
              } text-xs mt-1 text-${validationObj.lenPIN ? "gray" : "red"}-500`}
            >
              <Trans>
                The PIN must be <b>{pinLength}</b> digits long.
              </Trans>
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-5 sm:mt-4 flex-row-reverse">
        <Button
          type="submit"
          variant={variant}
          label={<Trans>Add</Trans>}
          onClick={(e) => onAddHandler(e)}
          disabled={startValidate && isDisabled}
        />
        <Button
          type="button"
          secondary={true}
          label={<Trans>Cancel</Trans>}
          onClick={() => setOpenPopup(false)}
        />
        <Alert
          type="info"
          className="flex-grow"
          title={
            <Trans>
              Fields marked with "<b className="text-red-500">*</b>" are
              required
            </Trans>
          }
        />
      </div>

      {loader ? <Loader /> : null}
    </form>
  );
};
